import React, { createContext, FC, ReactNode, useContext } from 'react';

import cn from 'classnames';

import { Checkbox, Text, Toggle } from '@components/common';
import { RestrictedAction } from '@components/shared/RestrictedAction';
import { InfoCircleIcon } from '@components/svgs';
import { Tooltip } from '@components/Tooltip';

const useChildFeatures = (children: ReactNode): ToggleableFeature[] => {
  return React.useMemo(() => {
    const childFeatures: ToggleableFeature[] = [];

    React.Children.forEach(children, (child) => {
      if (React.isValidElement<DisabledFeatureToggleProps>(child) && child.props.featureKey) {
        childFeatures.push(child.props.featureKey);
      }
    });

    return childFeatures;
  }, [children]);
};

interface ToggleControlProps {
  type: 'toggle' | 'checkbox';
  name: string;
  isFeatureDisabled: boolean;
  canToggle: boolean;
  onToggle: () => void;
}

const ToggleControl: FC<ToggleControlProps> = ({ type, name, isFeatureDisabled, canToggle, onToggle }) => {
  const className = cn({
    'opacity-50': !canToggle,
    'cursor-not-allowed': !canToggle
  });

  if (type === 'checkbox') {
    return (
      <Checkbox
        className={className}
        selected={!isFeatureDisabled}
        disabled={!canToggle}
        onChange={onToggle}
        name={name}
      />
    );
  }

  return <Toggle className={className} on={!isFeatureDisabled} disabled={!canToggle} onToggle={onToggle} name={name} />;
};

interface DisabledFeatureToggleContextType {
  disabledFeatures: Record<ToggleableFeature, boolean>;
  handleOnToggle: (feature: ToggleableFeature, childFeatures?: ToggleableFeature[]) => Promise<void>;
}

const DisabledFeatureToggleContext = createContext<DisabledFeatureToggleContextType | undefined>(undefined);

export const DisabledFeatureToggleProvider: FC<{
  children: ReactNode;
  value: DisabledFeatureToggleContextType;
}> = ({ children, value }) => (
  <DisabledFeatureToggleContext.Provider value={value}>{children}</DisabledFeatureToggleContext.Provider>
);

const useDisabledFeatureToggleContext = () => {
  const context = useContext(DisabledFeatureToggleContext);
  if (!context) {
    throw new Error('DisabledFeatureToggle must be used within DisabledFeatureToggleProvider');
  }
  return context;
};

interface DisabledFeatureToggleProps {
  name: string;
  featureKey: ToggleableFeature;
  description?: string;
  children?: ReactNode;
  parentDisabled?: boolean;
  level?: number;
  type?: 'toggle' | 'checkbox';
  disabled?: boolean;
}

export const DisabledFeatureToggle: FC<DisabledFeatureToggleProps> = ({
  name,
  featureKey,
  description,
  children,
  parentDisabled = false,
  level = 0,
  type = 'toggle',
  disabled = false
}) => {
  const { disabledFeatures, handleOnToggle } = useDisabledFeatureToggleContext();

  const isFeatureDisabled = disabledFeatures[featureKey] || false;
  const isToggleDisabled = parentDisabled || disabled;

  const childFeatures = useChildFeatures(children);

  const handleToggle = async () => {
    await handleOnToggle(featureKey, childFeatures);
  };

  const containerClasses = [type === 'checkbox' ? 'mb-2' : 'mb-4', level > 0 ? 'ml-6' : ''].join(' ');

  return (
    <div className={containerClasses}>
      <RestrictedAction permission='disableFeatures'>
        {({ can, may }) => {
          const canToggle = can && may && !isToggleDisabled;
          const textColor = canToggle ? 'gray-700' : 'gray-500';

          return (
            <div className='flex flex-row items-center space-x-2'>
              <ToggleControl
                type={type}
                name={name}
                isFeatureDisabled={isFeatureDisabled}
                canToggle={canToggle}
                onToggle={handleToggle}
              />
              <div className='flex items-center space-x-2'>
                <Text h='400' color={textColor}>
                  {name}
                </Text>
                {description && (
                  <Tooltip className='cursor-pointer' content={description} placement='right'>
                    <InfoCircleIcon className='h-3 w-3' />
                  </Tooltip>
                )}
              </div>
            </div>
          );
        }}
      </RestrictedAction>

      {children && (
        <div className='mt-2'>
          {React.Children.map(children, (child) =>
            React.isValidElement<DisabledFeatureToggleProps>(child)
              ? React.cloneElement(child, {
                  parentDisabled: isToggleDisabled || isFeatureDisabled,
                  level: level + 1
                })
              : child
          )}
        </div>
      )}
    </div>
  );
};
